import React, { createContext, useContext, useState, useEffect } from "react";
import { getUserSession, isAuthenticated as checkIsAuthenticated } from "../utils/userSession";
import { getValidAccessToken } from "../utils/tokenRefresh";
import { tokenStorage } from "../utils/tokenStorage";

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [session, setSession] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Check for existing session on mount
  useEffect(() => {
    const checkSession = async () => {
      try {
        setIsLoading(true);
        setError(null);

        if (checkIsAuthenticated()) {
          const userSession = getUserSession();
          setSession(userSession);
        } else {
          // User is not authenticated - session will remain null
          // App.js will handle redirecting to login
          setSession(null);
        }
      } catch (err) {
        console.error("Authentication check failed:", err);
        setError("Failed to authenticate");
      } finally {
        setIsLoading(false);
      }
    };

    checkSession();

    // Set up token refresh interval (check every 5 minutes)
    const interval = setInterval(async () => {
      if (checkIsAuthenticated()) {
        await getValidAccessToken(); // This will refresh if needed
        const updatedSession = getUserSession();
        setSession(updatedSession);
      }
    }, 5 * 60 * 1000);

    return () => clearInterval(interval);
  }, []);

  /**
   * Initiate login flow with Keycloak
   * @param {string} provider - Provider to use ('keycloak' or 'keycloak-new')
   */
  const login = (provider = 'keycloak-new') => {
    const authIssuer = process.env.REACT_APP_AUTH_ISSUER

    const clientId = provider === 'keycloak-new'
      ? (process.env.REACT_APP_AUTH_CLIENT_ID_NEW || process.env.REACT_APP_AUTH_CLIENT_ID || '')
      : (process.env.REACT_APP_AUTH_CLIENT_ID || '');

    if (!clientId) {
      console.error('Client ID not configured');
      setError('Authentication not configured');
      return;
    }

    // Generate PKCE code verifier and challenge
    const codeVerifier = generateCodeVerifier();
    generateCodeChallenge(codeVerifier).then(codeChallenge => {
      // Store code verifier for later use
      sessionStorage.setItem('pkce_code_verifier', codeVerifier);

      // Build authorization URL
      const redirectUri = encodeURIComponent(window.location.origin + '/auth/callback');
      const state = generateRandomString();
      sessionStorage.setItem('oauth_state', state);

      const authUrl = `${authIssuer}/protocol/openid-connect/auth?` +
        `client_id=${clientId}&` +
        `redirect_uri=${redirectUri}&` +
        `response_type=code&` +
        `scope=openid email profile&` +
        `code_challenge=${codeChallenge}&` +
        `code_challenge_method=S256&` +
        `state=${state}`;

      window.location.href = authUrl;
    }).catch(err => {
      console.error('Failed to generate code challenge:', err);
      setError('Failed to initiate login');
    });
  };

  /**
   * Logout user and clear session
   * This function immediately redirects, so React state updates may not complete
   */
  const logout = () => {
    // Get values before clearing tokens
    const endSessionUrl = process.env.REACT_APP_END_SESSION_URL
    const idToken = tokenStorage.getIdToken();
    // Always redirect to root after logout, not back to current page
    const redirectUri = encodeURIComponent(window.location.origin + '/');

    // Clear tokens immediately (synchronous operation)
    tokenStorage.clearTokens();

    // Always redirect immediately - don't wait for anything
    if (idToken && endSessionUrl) {
      // Clean the endSessionUrl to remove any trailing =, ?, or whitespace
      const cleanEndSessionUrl = endSessionUrl.trim().replace(/[=?]+$/, '');
      // Redirect to Keycloak logout endpoint
      // After logout, Keycloak will redirect back to root (/)
      const logoutUrl = `${cleanEndSessionUrl}?id_token_hint=${encodeURIComponent(idToken)}&post_logout_redirect_uri=${redirectUri}`;
      // Use replace to prevent back button issues
      window.location.replace(logoutUrl);
    } else {
      // If no idToken, redirect to home (which will trigger login redirect)
      window.location.replace(window.location.origin + '/');
    }
  };

  /**
   * Refresh the current session
   */
  const refreshSession = async () => {
    try {
      const token = await getValidAccessToken();
      if (token) {
        const userSession = getUserSession();
        setSession(userSession);
      } else {
        setSession(null);
      }
    } catch (err) {
      console.error('Failed to refresh session:', err);
      setSession(null);
    }
  };

  const value = {
    session,
    isLoading,
    isAuthenticated: session !== null,
    error,
    login,
    logout,
    refreshSession,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// PKCE Helper Functions
function generateCodeVerifier() {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return base64UrlEncode(array);
}

async function generateCodeChallenge(verifier) {
  const encoder = new TextEncoder();
  const data = encoder.encode(verifier);
  const digest = await crypto.subtle.digest('SHA-256', data);
  return base64UrlEncode(new Uint8Array(digest));
}

function base64UrlEncode(array) {
  return btoa(String.fromCharCode(...array))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '');
}

function generateRandomString() {
  const array = new Uint8Array(16);
  crypto.getRandomValues(array);
  return base64UrlEncode(array);
}
